import React, { useEffect, useState } from "react";
import { Mail, MessageCircle, ClipboardPen, ArrowUpRight } from "lucide-react";
import * as FaIcons from "react-icons/fa";
import * as FiIcons from "react-icons/fi";
import * as SiIcons from "react-icons/si";
import { Phone } from "lucide-react";
import { MapPin } from "lucide-react";

const Footer = () => {
  const [socials, setSocials] = useState([]);
  const [mapLink, setMapLink] = useState(null);
  const [serviceLinks, setServiceLinks] = useState([]);

  const iconMap = {
    twitter: FaIcons.FaTwitter,
    instagram: FaIcons.FaInstagram,
    linkedin: FaIcons.FaLinkedin,
    github: FaIcons.FaGithub,
    facebook: FaIcons.FaFacebook,
    discord: FaIcons.FaDiscord,
    telegram: FaIcons.FaTelegramPlane,
    rss: FaIcons.FaRss,
  };

  const fallbackIcon = FiIcons.FiLink;

  const toPascal = (str) =>
    str
      .replace(/[-_\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ""))
      .replace(/^(.)/, (m) => m.toUpperCase());

  const resolveIcon = (label) => {
    if (!label) return fallbackIcon;
    const key = label.toLowerCase().trim();
    if (iconMap[key]) return iconMap[key];

    const pascal = toPascal(key);
    const candidates = [
      `Fa${pascal}`,
      `Fi${pascal}`,
      `Si${pascal}`,
      `Fa${pascal}Square`,
    ];

    for (const name of candidates) {
      if (FaIcons[name]) return FaIcons[name];
      if (FiIcons[name]) return FiIcons[name];
      if (SiIcons[name]) return SiIcons[name];
    }

    return fallbackIcon;
  };

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const res = await fetch("https://rxo.me/data.json");
        if (!res.ok) return;
        const json = await res.json();
        const root = Array.isArray(json) ? json[0] : json;
        const socialObj = root?.social_media || {};
        const entries = Object.entries(socialObj || {}).filter(([, url]) => !!url);
        const list = entries.map(([label, url]) => ({ label, url }));
        const gmLink = root?.google_map?.link || null;
        // determine which services list to show based on cached geo country
        const getCachedCountry = () => {
          try {
            const raw = localStorage.getItem("rxo_geo_country");
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            return parsed?.country || null;
          } catch (e) {
            return null;
          }
        };

        const country = (getCachedCountry() || window.__RXO_GEO_COUNTRY || "").toString();
        const isIndia = (c) => {
          if (!c) return false;
          const v = c.toString().toLowerCase();
          return v === "in" || v === "india" || v.includes("india");
        };

        let servicesArr = root?.services?.global || [];
        let linksArr = root?.services?.global_link || [];
        if (isIndia(country) && root?.services?.india) {
          servicesArr = root.services.india || servicesArr;
          linksArr = root.services.india_link || linksArr;
        }

        const servicesList = (servicesArr || []).map((label, idx) => ({
          label,
          url: (linksArr && linksArr[idx]) || "#",
        }));

        if (mounted) {
          setSocials(list);
          setMapLink(gmLink);
          setServiceLinks(servicesList);
        }
      } catch (e) {
        // silent fail — keep socials empty
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, []);

  const openTawk = () => {
    try {
      const win = window;
      if (win.Tawk_API) {
        if (typeof win.Tawk_API.maximize === "function") {
          win.Tawk_API.maximize();
          return;
        }
        if (typeof win.Tawk_API.toggle === "function") {
          win.Tawk_API.toggle();
          return;
        }
      }
      const url = "https://tawk.to/chat/68b2d8b3721af15d8752b653/1jp0087bs";
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {}
  };

  return (
    <footer
      id="contact"
      data-testid="footer-section"
      className="relative overflow-hidden bg-ink text-cream"
    >
      <div className="pointer-events-none absolute -top-40 right-0 h-[420px] w-[420px] rounded-full bg-acid/20 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-[420px] w-[420px] rounded-full bg-ultra/30 blur-[120px]" />

      <div className="relative mx-auto max-w-[1480px] px-6 pt-24 pb-12 md:px-12 md:pt-36">
        <div className="font-mono text-xs uppercase tracking-[0.22em] text-acid">
          06 — Let's build
        </div>

        <h2 className="mt-6 font-display text-[14vw] font-extrabold uppercase leading-[0.86] tracking-tightest md:text-[10vw]">
          Got an idea
          <br />
          worth <span className="italic text-acid">shipping?</span>
        </h2>

        <div className="mt-12 grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            <p className="max-w-xl font-body text-lg leading-relaxed text-cream/70 md:text-xl">
              Tell us what you're building. We'll reply within 24 hours with a
              one-pager and a calendar link. No forms, no funnels — just two
              humans on a call.
            </p>

            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <a
                href="mailto:hello@rxo.me?subject=Inquiry%20for%20Android%20App%20Development&body=Hi%20Rexomi%2C%0A%0AI%E2%80%99m%20looking%20for%20Android%20app%20development%20services%20and%20would%20love%20to%20discuss%20my%20requirements.%0A%0AMy%20Name%3A%0AMy%20Phone%20Number%3A%0AMy%20Country%3A%0A%0AMy%20Company%20%2F%20Brand%20Name%3A%0AServices%20Interested%20In%3A%20Android%20App%20Development%0AApp%20Category%20I%20Am%20Interested%20In%3A%0A%0AAdditional%20Notes%3A"
                data-testid="footer-email-cta"
                className="group inline-flex items-center justify-between gap-4 rounded-sm border border-cream/30 bg-cream px-6 py-5 font-mono text-xs uppercase tracking-[0.18em] text-ink transition-all hover:bg-acid"
              >
                <span className="flex items-center gap-3">
                  <Mail className="h-4 w-4" />
                  mail us
                </span>
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
              </a>

              <button type="button"
                data-youform-open="3wzd4p3n"
                data-youform-position="center"
                data-testid="footer-whatsapp-cta"
                className="group inline-flex items-center justify-between gap-4 rounded-sm border border-cream/30 bg-transparent px-6 py-5 font-mono text-xs uppercase tracking-[0.18em] text-cream transition-all hover:bg-cream hover:text-ink"
              >
                <span className="flex items-center gap-3">
                  <ClipboardPen className="h-4 w-4" />
                  LET'S TALK
                </span>
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 md:col-span-5">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cream/40">
                Studio
              </div>
              <div className="mt-3 font-display text-lg font-semibold">
              Kolkata, IN
              <br/>
              <a
                href={mapLink || '#'}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-visit-link"
                className="inline-flex items-center gap-2 text-cream/80 transition-colors hover:text-acid"
              >
                <span>Visit Us</span>
                <MapPin className="h-4 w-4" />
              </a>
            </div>
            </div>

            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cream/40">
                Connect
              </div>
              <button
                  type="button"
                  data-testid="footer-book-cta"
                  onClick={openTawk}
                  className="mt-3 flex items-center gap-2 font-display text-lg font-semibold hover:text-acid"
                >
                  <span>Book a call</span>
                  <Phone className="h-4 w-4" />
                </button>
              <a
                  href="https://wa.me/918697878785?text=Hi%20Rexomi%2C%0A%0AI%E2%80%99m%20looking%20for%20Android%20app%20development%20services%20and%20would%20love%20to%20discuss%20my%20requirements.%0A%0AMy%20Name%3A%0AMy%20Company%20%2F%20Brand%20Name%3A%0AApp%20Category%20I%20Am%20Interested%20In%3A%0AProject%20Details%3A"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-connect-whatsapp"
                  className="inline-flex items-center gap-2 font-display text-lg font-semibold text-cream/80 hover:text-acid"
                >
                  <span>WhatsApp</span>
                  <MessageCircle className="h-4 w-4" />
                </a>
            </div>

            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cream/40">
                Services
              </div>
                <div className="mt-3 font-display text-lg font-semibold hover:text-acid">
                  {serviceLinks.length > 0 ? (
                    <ul className="space-y-2 list-none m-0 p-0">
                      {serviceLinks.map(({ label, url }) => (
                        <li key={label}>
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-cream/80 hover:text-acid"
                          >
                            {label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-cream/80">—</span>
                  )}
                </div>
            </div>

            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cream/40">
                Company
              </div>
              <div className="mt-3 font-display text-lg font-semibold">
                <a href="https://rxo.me/about" target="_blank" rel="noreferrer" className="text-cream/80 hover:text-acid">
                  About Us
                </a>
              </div>
              <div className="mt-3 font-display text-lg font-semibold">
                <a href="https://rxo.me/support" target="_blank" rel="noreferrer" className="text-cream/80 hover:text-acid">
                  Support
                </a>
              </div>
              <div className="mt-3 font-display text-lg font-semibold">
                <a href="https://rxo.me/careers" target="_blank" rel="noreferrer" className="text-cream/80 hover:text-acid">
                  Careers
                </a>
              </div>
              <div className="mt-3 font-display text-lg font-semibold">
                <a href="https://rxo.me/legal-privacy" target="_blank" rel="noreferrer" className="text-cream/80 hover:text-acid">
                  Legal & Privacy
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20 flex flex-col items-start justify-between gap-6 border-t border-cream/15 pt-8 md:flex-row md:items-center">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cream/60">
              © {new Date().getFullYear()} <a href="https://rxo.me" target="_blank" rel="noreferrer" style={{color: "#c8f04a"}}>
                Rexomi Studio
              </a> — Made with sharp pencils.
            </span>
          </div>

          <div className="flex w-full items-center justify-center gap-4 md:w-auto md:justify-start">
            {socials.map(({ label, url }) => {
              const Icon = resolveIcon(label);
              if (!Icon) return null;
              return (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`footer-social-${label}`}
                  aria-label={label}
                  className="grid h-10 w-10 place-items-center rounded-sm border border-cream/20 transition-colors hover:border-acid hover:bg-acid hover:text-ink"
                >
                  <Icon className="h-4 w-4" />
                </a>
              );
            })}
          </div>
        </div>

        <div className="mt-16 select-none overflow-hidden">
          <div className="font-display text-[26vw] font-extrabold leading-[0.8] tracking-tightest text-cream/[0.06] md:text-[20vw]">
            REXOMI<span className="text-acid/40">.</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;